// © 2026 Orpyx Medical Technologies Inc.

import {PropertyNamePipe} from "orpyx-web-common";
import {Field, useFormikContext} from "formik";
import {Select} from "formik-material-ui";
import MenuItem from "@material-ui/core/MenuItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import * as React from "react";
import {memo} from "react";
import {SuggestedActionSelectorProps} from "./suggested-action-selector.interface";
import {groupSuggestedActionsByOwner, SuggestedActionGroup} from "./suggested-actions";
import {PatientSubmitCareNoteFormValues} from "../patient-submit-care-note-form/patient-submit-care-note-form.interface";

//  MUI's Select treats every child as an option, so a click on a heading would otherwise select
//  its undefined value. Stopping it in the capture phase keeps the menu's own click handler from
//  ever seeing it.
const ignoreHeadingClick = (event: React.MouseEvent<HTMLLIElement>): void => {
    event.stopPropagation();
};

//  Flattened rather than wrapped per group - MUI's Select rejects Fragment children.
const renderGroups = (groups: SuggestedActionGroup[]): React.ReactElement[] => {
    const items: React.ReactElement[] = [];

    for (const group of groups) {
        items.push(<ListSubheader key={`owner-${group.owner}`} onClickCapture={ignoreHeadingClick}>
            {group.owner}
        </ListSubheader>);

        for (const action of group.actions) {
            items.push(<MenuItem key={action.id} value={action.id}>
                {action.name}
            </MenuItem>);
        }
    }

    return items;
};

export const SuggestedActionSelector = memo((props: SuggestedActionSelectorProps) => {
    const {errors, touched} = useFormikContext<PatientSubmitCareNoteFormValues>();

    const fieldName: string = PropertyNamePipe(props.values, 'suggestedActionId');
    const inputProps = {
        id: fieldName,
        name: fieldName
    };

    //  A single option is the only possible answer, so the form fills it in and the nurse
    //  cannot change it. Passed as undefined otherwise, not false - an explicit false would stop
    //  formik-material-ui disabling the field while the form submits.
    const onlyOption: boolean = props.suggestedActions.length === 1;

    //  formik-material-ui's Select drops error / helperText, unlike its TextField, so the
    //  surrounding FormControl carries the error state and the message instead.
    const showError: boolean = Boolean(touched.suggestedActionId && errors.suggestedActionId);

    return (
        <FormControl error={showError} fullWidth={true}>
            <Field
                component={Select}
                className={props.classes.input}
                name={fieldName}
                value={props.selectedSuggestedAction}
                inputProps={inputProps}
                disabled={onlyOption || undefined}
            >
                {renderGroups(groupSuggestedActionsByOwner(props.suggestedActions))}
            </Field>

            {showError && <FormHelperText>
                {errors.suggestedActionId}
            </FormHelperText>}
        </FormControl>
    );
});
